import { emitOrgAuditLog } from '../../../../crud/orgs.crud'
import { actions as GWS_ACTIONS } from '../integrations/gws/actions'
import { actions as M365_ACTIONS } from '../integrations/m365/actions'

const EVENT_TYPES = {
  SHARED_MAILBOX: 'm365.shared_mailbox_access.update',
  GWS_GROUPS: 'gws.groups.update',
  GWS_LICENSE: 'gws.license.update',
  GWS_RESET_PASSWORD: 'gws.reset_password.update',
  GWS_SUSPEND: 'gws.user.update',
  GWS_REVOKE_SESSIONS: 'gws.revoke_sessions.update',
  GWS_OFFBOARD: 'gws.offboarding.update',
}

const AUDIT_TAGS = {
  M365_USER: ['m365', 'user'],
  GWS_USER: ['gws', 'user'],
}

const ENTITY_TYPES = {
  USER: 'user',
  GROUP: 'group',
}

const ENTITY_ACTIONS = {
  ADDED: 'added',
  REMOVED: 'removed',
  UPDATED: 'updated',
}

const ENGAGE_AUDIT_SOURCE = {
  feature: 'engage',
  component: 'IntegrationManager',
  path: '/engage/user-management',
}

const asArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== '')
  }

  if (value === undefined || value === null || value === '') {
    return []
  }

  return [value]
}

const text = (value) => (typeof value === 'string' ? value.trim() : '')

const labelOf = (item) => {
  if (typeof item === 'string' || typeof item === 'number') {
    return String(item)
  }

  return text(item?.displayName) || text(item?.name) || text(item?.id) || ''
}

const joinLabels = (items) => asArray(items).map(labelOf).filter(Boolean).join(', ')

const employeeFromSelection = (employee) => {
  const email = text(employee?.gwsPrimaryEmail) || text(employee?.email)
  const name = text(employee?.displayName) || [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim()

  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} (${email})`
  }

  return email || name
}

const describeEmployees = ({ parameters = {}, actionData, selectedEmployee }) => {
  const named = asArray(actionData?.employeesUpdated)
    .map((employee) => text(employee?.employeeFullName))
    .filter(Boolean)

  if (named.length === 1) {
    return named[0]
  }

  if (named.length > 1) {
    return `${named.length} employees`
  }

  const emails = asArray(parameters.employeeEmail)
  if (emails.length > 1) {
    return `${emails.length} employees`
  }

  return employeeFromSelection(selectedEmployee) || emails[0] || text(parameters.employeeIdentifier)
}

const userEntities = ({ parameters = {}, actionData, selectedEmployee, action }) => {
  const emails = asArray(parameters.employeeEmail)
  const names = asArray(actionData?.employeesUpdated).map((employee) => text(employee?.employeeFullName))
  const selectedLabel = employeeFromSelection(selectedEmployee)
  // A licence change for a dual-integrated employee can succeed with only the Google id.
  // The visible name is a label, never the stored user id.
  const identifier = text(parameters.employeeIdentifier)
  const targets = emails.length ? emails : [identifier].filter(Boolean)

  return targets.map((target, index) => ({
    id: String(target),
    type: ENTITY_TYPES.USER,
    displayName: names[index] || selectedLabel || String(target),
    action,
  }))
}

const groupEntities = (ids, labels, action) =>
  asArray(ids).map((id, index) => ({
    id: String(id),
    type: ENTITY_TYPES.GROUP,
    displayName: labelOf(asArray(labels)[index]) || String(id),
    action,
  }))

const withoutPassword = (payload = {}) => {
  const { passwordInfo } = payload
  if (!passwordInfo || typeof passwordInfo !== 'object' || !('newPassword' in passwordInfo)) {
    return payload
  }

  const { newPassword: _newPassword, ...safePasswordInfo } = passwordInfo

  return {
    ...payload,
    passwordInfo: safePasswordInfo,
  }
}

const auditLog = ({ eventType, summary, tags, payload, entities, customerId, customerName }) => ({
  eventType,
  summary,
  tags,
  customerId,
  customerName,
  payload: withoutPassword(payload),
  ...(entities?.length ? { entities } : {}),
  source: ENGAGE_AUDIT_SOURCE,
})

const membershipSummary = ({ employee, addedLabel, removedLabel, bothSummary }) => {
  if (addedLabel && !removedLabel) {
    return addedLabel
  }

  if (removedLabel && !addedLabel) {
    return removedLabel
  }

  return employee ? `${bothSummary} for ${employee}` : bothSummary
}

const sameIdentity = (left, right) => text(left).toLowerCase() === text(right).toLowerCase()

const labelsForIds = (ids, submittedIds, labels) =>
  ids.map((id) => {
    const index = submittedIds.indexOf(id)
    return index >= 0 ? asArray(labels)[index] : id
  })

const confirmedIds = (confirmed, submittedIds) => {
  const allowed = new Set(submittedIds)
  return asArray(confirmed)
    .map(String)
    .filter((id) => allowed.has(id))
}

// Exchange Online records a mailbox permission on the completed task only after the
// cmdlet succeeds. A removal that throws is left off that list and the task can still complete.
const confirmedMailboxAccess = (taskResult) => {
  const message = text(taskResult?.StatusMessage)
  if (!message) {
    return null
  }

  try {
    const parsed = JSON.parse(message)
    return Array.isArray(parsed?.mailboxAccess) ? parsed.mailboxAccess : null
  } catch (error) {
    return null
  }
}

const sharedMailboxAuditEntry = ({ parameters = {}, actionData, selectedEmployee, customerId, customerName }) => {
  const added = asArray(actionData?.groupsAdded)
  const removed = asArray(actionData?.groupsRemoved)
  const addedIds = asArray(parameters.groupsInfo?.toBeAdded)
  const removedIds = asArray(parameters.groupsInfo?.toBeRemoved)

  if (!addedIds.length && !removedIds.length) {
    return []
  }

  const employee = describeEmployees({ parameters, actionData, selectedEmployee })
  const addedNames = joinLabels(added.length ? added : addedIds)
  const removedNames = joinLabels(removed.length ? removed : removedIds)
  const summary = membershipSummary({
    employee,
    addedLabel: addedNames ? `Added ${employee || 'employee'} to shared mailbox ${addedNames}` : '',
    removedLabel: removedNames ? `Removed ${employee || 'employee'} from shared mailbox ${removedNames}` : '',
    bothSummary: 'Updated shared mailbox access',
  })

  return [
    auditLog({
      eventType: EVENT_TYPES.SHARED_MAILBOX,
      summary,
      tags: AUDIT_TAGS.M365_USER,
      customerId,
      customerName,
      payload: {
        employeeEmail: parameters.employeeEmail,
        groupType: parameters.groupType || 'SharedMailbox',
        groupsInfo: parameters.groupsInfo,
        groupsAdded: added.map(labelOf).filter(Boolean),
        groupsRemoved: removed.map(labelOf).filter(Boolean),
      },
      entities: [
        ...userEntities({ parameters, actionData, selectedEmployee, action: ENTITY_ACTIONS.UPDATED }),
        ...groupEntities(addedIds, added, ENTITY_ACTIONS.ADDED),
        ...groupEntities(removedIds, removed, ENTITY_ACTIONS.REMOVED),
      ],
    }),
  ]
}

const sharedMailboxAuditLog = ({
  parameters = {},
  actionData,
  selectedEmployee,
  customerId,
  customerName,
  taskResult,
}) => {
  const confirmed = confirmedMailboxAccess(taskResult)
  if (!confirmed) {
    return []
  }

  const submittedAdded = asArray(parameters.groupsInfo?.toBeAdded).map(String)
  const submittedRemoved = asArray(parameters.groupsInfo?.toBeRemoved).map(String)
  const emails = asArray(parameters.employeeEmail)
  const names = asArray(actionData?.employeesUpdated)

  return confirmed.flatMap((change) => {
    const email = text(change?.employeeEmail)
    const employeeIndex = emails.findIndex((candidate) => sameIdentity(candidate, email))
    const addedIds = confirmedIds(change?.added, submittedAdded)
    const removedIds = confirmedIds(change?.removed, submittedRemoved)

    if (employeeIndex < 0 || (!addedIds.length && !removedIds.length)) {
      return []
    }

    return sharedMailboxAuditEntry({
      parameters: {
        ...parameters,
        employeeEmail: [emails[employeeIndex]],
        groupsInfo: { toBeAdded: addedIds, toBeRemoved: removedIds },
      },
      actionData: {
        ...actionData,
        employeesUpdated: names[employeeIndex] ? [names[employeeIndex]] : [],
        groupsAdded: labelsForIds(addedIds, submittedAdded, actionData?.groupsAdded),
        groupsRemoved: labelsForIds(removedIds, submittedRemoved, actionData?.groupsRemoved),
      },
      selectedEmployee,
      customerId,
      customerName,
    })
  })
}

const GWS_GROUP_FAILURE_SEPARATOR = /\s*\/n\s*\/r\s*/
const GWS_GROUP_USER_FAILURE = /^User:([^,]+),\s*(.*)$/

const taskErrorMessage = (taskError) => {
  if (!taskError || typeof taskError === 'string') {
    return ''
  }

  return text(taskError.rawMessage) || text(taskError.errorMessage)
}

// The Probe applies each employee, then fails the whole task if any employee had a group error.
// The message names those employees and which side failed. Everyone else was applied.
const parseGwsGroupFailures = (message) => {
  if (!message.includes('User:')) {
    return null
  }

  const failures = message.split(GWS_GROUP_FAILURE_SEPARATOR).flatMap((segment) => {
    const match = segment.trim().match(GWS_GROUP_USER_FAILURE)
    if (!match) {
      return []
    }

    const detail = match[2]
    return [
      {
        email: match[1].trim(),
        addFailed: detail.includes('Add Group Errors:'),
        removeFailed: detail.includes('Remove Group Errors:'),
      },
    ]
  })

  return failures.length ? failures : null
}

const GROUP_RESULTS_MARKER = '{"groupResults":'

// A failed group task names the employees who had errors. When the Probe also lists the
// group ids it applied, those ids are the changes to audit, including a partial side.
const parseGwsGroupResults = (message) => {
  const start = message.indexOf(GROUP_RESULTS_MARKER)
  if (start < 0) {
    return null
  }

  try {
    const parsed = JSON.parse(message.slice(start))
    return Array.isArray(parsed?.groupResults) ? parsed.groupResults : null
  } catch (error) {
    return null
  }
}

const slicesFromConfirmedGroups = ({
  emails,
  names,
  submittedAdded,
  submittedRemoved,
  addedLabels,
  removedLabels,
  parameters,
  actionData,
  confirmed,
}) =>
  confirmed.flatMap((change) => {
    const employeeIndex = emails.findIndex((candidate) => sameIdentity(candidate, change?.employeeEmail))
    const addedIds = confirmedIds(change?.added, submittedAdded)
    const removedIds = confirmedIds(change?.removed, submittedRemoved)

    if (employeeIndex < 0 || (!addedIds.length && !removedIds.length)) {
      return []
    }

    return [
      {
        parameters: {
          ...parameters,
          employeeEmail: [emails[employeeIndex]],
          groupsInfo: { toBeAdded: addedIds, toBeRemoved: removedIds },
        },
        actionData: {
          ...actionData,
          employeesUpdated: names[employeeIndex] ? [names[employeeIndex]] : [],
          groupsAdded: labelsForIds(addedIds, submittedAdded, addedLabels),
          groupsRemoved: labelsForIds(removedIds, submittedRemoved, removedLabels),
        },
      },
    ]
  })

const gwsGroupAuditSlices = ({ parameters = {}, actionData, taskError }) => {
  const emails = asArray(parameters.employeeEmail)
  const addedIds = asArray(parameters.groupsInfo?.toBeAdded)
  const removedIds = asArray(parameters.groupsInfo?.toBeRemoved)

  if (!addedIds.length && !removedIds.length) {
    return []
  }

  if (!taskError) {
    return [{ parameters, actionData }]
  }

  const message = taskErrorMessage(taskError)
  const confirmed = parseGwsGroupResults(message)
  if (confirmed) {
    return slicesFromConfirmedGroups({
      emails,
      names: asArray(actionData?.employeesUpdated),
      submittedAdded: addedIds.map(String),
      submittedRemoved: removedIds.map(String),
      addedLabels: asArray(actionData?.groupsAdded),
      removedLabels: asArray(actionData?.groupsRemoved),
      parameters,
      actionData,
      confirmed,
    })
  }

  const failures = parseGwsGroupFailures(message)
  if (!failures) {
    return []
  }

  const names = asArray(actionData?.employeesUpdated)
  const addedLabels = asArray(actionData?.groupsAdded)
  const removedLabels = asArray(actionData?.groupsRemoved)

  return emails.flatMap((email, index) => {
    const failure = failures.find((item) => sameIdentity(item.email, email))
    const includeAdd = addedIds.length > 0 && !failure?.addFailed
    const includeRemove = removedIds.length > 0 && !failure?.removeFailed

    if (!includeAdd && !includeRemove) {
      return []
    }

    return [
      {
        parameters: {
          ...parameters,
          employeeEmail: [email],
          groupsInfo: {
            toBeAdded: includeAdd ? addedIds : [],
            toBeRemoved: includeRemove ? removedIds : [],
          },
        },
        actionData: {
          ...actionData,
          employeesUpdated: names[index] ? [names[index]] : [],
          groupsAdded: includeAdd ? addedLabels : [],
          groupsRemoved: includeRemove ? removedLabels : [],
        },
      },
    ]
  })
}

const gwsGroupsAuditEntry = ({ parameters = {}, actionData, selectedEmployee, customerId, customerName }) => {
  const added = asArray(actionData?.groupsAdded)
  const removed = asArray(actionData?.groupsRemoved)
  const addedIds = asArray(parameters.groupsInfo?.toBeAdded)
  const removedIds = asArray(parameters.groupsInfo?.toBeRemoved)

  if (!addedIds.length && !removedIds.length) {
    return []
  }

  const employee = describeEmployees({ parameters, actionData, selectedEmployee })
  const addedNames = joinLabels(added.length ? added : addedIds)
  const removedNames = joinLabels(removed.length ? removed : removedIds)
  const summary = membershipSummary({
    employee,
    addedLabel: addedNames ? `Added ${employee || 'employee'} to Google Workspace group ${addedNames}` : '',
    removedLabel: removedNames ? `Removed ${employee || 'employee'} from Google Workspace group ${removedNames}` : '',
    bothSummary: 'Updated Google Workspace groups',
  })

  return [
    auditLog({
      eventType: EVENT_TYPES.GWS_GROUPS,
      summary,
      tags: AUDIT_TAGS.GWS_USER,
      customerId,
      customerName,
      payload: {
        employeeEmail: parameters.employeeEmail,
        groupsInfo: parameters.groupsInfo,
        groupsAdded: added.map(labelOf).filter(Boolean),
        groupsRemoved: removed.map(labelOf).filter(Boolean),
      },
      entities: [
        ...userEntities({ parameters, actionData, selectedEmployee, action: ENTITY_ACTIONS.UPDATED }),
        ...groupEntities(addedIds, added, ENTITY_ACTIONS.ADDED),
        ...groupEntities(removedIds, removed, ENTITY_ACTIONS.REMOVED),
      ],
    }),
  ]
}

const gwsGroupsAuditLog = (context) =>
  gwsGroupAuditSlices(context).flatMap((slice) => gwsGroupsAuditEntry({ ...context, ...slice }))

const licenseIdOf = (license) => {
  if (typeof license === 'string' || typeof license === 'number') {
    return String(license)
  }

  return text(license?.id) || text(license?.skuId)
}

const labelsForSubmittedIds = (labels, ids) => {
  const submittedIds = new Set(asArray(ids).map(String))

  return asArray(labels).filter((label) => submittedIds.has(licenseIdOf(label)))
}

const gwsLicenseAuditLog = ({ parameters = {}, actionData, selectedEmployee, customerId, customerName }) => {
  const addedIds = asArray(parameters.licenseInfo?.toBeAdded)
  const removedIds = asArray(parameters.licenseInfo?.toBeRemoved)
  const assigned = labelsForSubmittedIds(actionData?.licensesAssigned, addedIds)
  const removed = labelsForSubmittedIds(actionData?.licensesRemoved, removedIds)

  if (!addedIds.length && !removedIds.length && !assigned.length && !removed.length) {
    return []
  }

  const employee = describeEmployees({ parameters, actionData, selectedEmployee })
  const assignedNames = joinLabels(assigned.length ? assigned : addedIds)
  const removedNames = joinLabels(removed.length ? removed : removedIds)
  const summary = membershipSummary({
    employee,
    addedLabel: assignedNames ? `Assigned Google Workspace license ${assignedNames} to ${employee || 'employee'}` : '',
    removedLabel: removedNames ? `Removed Google Workspace license ${removedNames} from ${employee || 'employee'}` : '',
    bothSummary: 'Updated Google Workspace licenses',
  })

  return [
    auditLog({
      eventType: EVENT_TYPES.GWS_LICENSE,
      summary,
      tags: AUDIT_TAGS.GWS_USER,
      customerId,
      customerName,
      payload: {
        employeeEmail: parameters.employeeEmail,
        employeeIdentifier: parameters.employeeIdentifier,
        licenseInfo: parameters.licenseInfo,
        licensesAssigned: assigned.map(labelOf).filter(Boolean),
        licensesRemoved: removed.map(labelOf).filter(Boolean),
      },
      entities: userEntities({ parameters, actionData, selectedEmployee, action: ENTITY_ACTIONS.UPDATED }),
    }),
  ]
}

const gwsOffboardAuditLogs = ({ parameters = {}, selectedEmployee, customerId, customerName }) => {
  const employee = describeEmployees({ parameters, selectedEmployee })
  const employeeFields = {
    employeeEmail: parameters.employeeEmail,
    employeeIdentifier: parameters.employeeIdentifier,
  }
  const updatedEntities = userEntities({
    parameters,
    selectedEmployee,
    action: ENTITY_ACTIONS.UPDATED,
  })
  const logs = []

  if (parameters.resetPassword) {
    logs.push(
      auditLog({
        eventType: EVENT_TYPES.GWS_RESET_PASSWORD,
        summary: employee ? `Google Workspace reset password for ${employee}` : 'Google Workspace reset password',
        tags: AUDIT_TAGS.GWS_USER,
        customerId,
        customerName,
        payload: withoutPassword({
          ...employeeFields,
          passwordInfo: parameters.passwordInfo,
        }),
        entities: updatedEntities,
      }),
    )
  }

  if (parameters.suspended) {
    logs.push(
      auditLog({
        eventType: EVENT_TYPES.GWS_SUSPEND,
        summary: employee ? `Google Workspace suspend sign-in for ${employee}` : 'Google Workspace suspend sign-in',
        tags: AUDIT_TAGS.GWS_USER,
        customerId,
        customerName,
        payload: {
          ...employeeFields,
          suspended: true,
        },
        entities: updatedEntities,
      }),
    )
  }

  if (parameters.revokeSessions) {
    logs.push(
      auditLog({
        eventType: EVENT_TYPES.GWS_REVOKE_SESSIONS,
        summary: employee ? `Google Workspace revoke sessions for ${employee}` : 'Google Workspace revoke sessions',
        tags: AUDIT_TAGS.GWS_USER,
        customerId,
        customerName,
        payload: employeeFields,
        entities: updatedEntities,
      }),
    )
  }

  if (parameters.delete) {
    logs.push(
      auditLog({
        eventType: EVENT_TYPES.GWS_OFFBOARD,
        summary: employee ? `Google Workspace offboard user ${employee}` : 'Google Workspace offboard user',
        tags: AUDIT_TAGS.GWS_USER,
        customerId,
        customerName,
        payload: {
          ...employeeFields,
          delete: true,
          dataTransfer: parameters.dataTransfer,
          dataTransferOptions: parameters.dataTransferOptions,
        },
        entities: userEntities({ parameters, selectedEmployee, action: ENTITY_ACTIONS.REMOVED }),
      }),
    )
  }

  return logs
}

/**
 * Audit entries for Engage actions that still run on the Probe. Hub actions already
 * write their own entries when the hub task finishes.
 */
export const buildEngageProbeAuditLogs = ({
  action,
  parameters,
  actionData,
  selectedEmployee,
  customerId,
  customerName,
  taskError,
  taskResult,
}) => {
  // A failed Probe task did not apply the request, except a Google group update. That task
  // names who failed and, when it can, the group ids that were still applied.
  if (taskError && action !== GWS_ACTIONS.GWS_MANAGE_GROUPS) {
    return []
  }

  const context = { parameters, actionData, selectedEmployee, customerId, customerName, taskError, taskResult }

  switch (action) {
    case M365_ACTIONS.M365_MANAGE_SHARED_GROUPS:
      return sharedMailboxAuditLog(context)
    case GWS_ACTIONS.GWS_MANAGE_GROUPS:
      return gwsGroupsAuditLog(context)
    case GWS_ACTIONS.GWS_MANAGE_LICENSE:
      return gwsLicenseAuditLog(context)
    case GWS_ACTIONS.GWS_DELETE_USER:
      return gwsOffboardAuditLogs(context)
    default:
      return []
  }
}

export const recordEngageProbeAuditLogs = async (input) => {
  const organizationId = input?.organizationId
  if (!organizationId) {
    return
  }

  const auditLogs = buildEngageProbeAuditLogs(input)

  await Promise.all(
    auditLogs.map((entry) =>
      emitOrgAuditLog({ organizationId, auditLog: entry }).catch((error) => {
        console.error('Failed to emit Engage audit log', error)
      }),
    ),
  )
}
